import { CANDLE_PANE_ID } from '../types/chart'

/**
 * Pure rules for the "+" joined to the crosshair price label
 * (components/CrosshairPriceAction.tsx) and the "Add alert at $X" menu rows.
 * Chart crosshair alert spec, part B.
 */

/** What the host receives when the "+" or a right-click row asks for a price alert. */
export interface AddPriceAlertRequest {
  /** The price under the pointer, rounded to the chart's price precision. */
  price: number
  /** Viewport box the popover hangs from: the "+" button, or the right-click point (0 by 0). */
  anchorRect: { left: number; top: number; width: number; height: number }
  /** The side the price axis is on; the popover opens toward the candles. */
  axisSide: 'left' | 'right'
}

/** The crosshair price label's box: 11px text, 2px padding top and bottom, no border
 *  (themes/dark.ts:118-138). */
export const PLUS_BUTTON = { width: 15, height: 15 } as const

/** How far the pointer may drift up or down from a latched "+" before it lets go, in px. */
export const LATCH_BAND_PX = 12

export interface PlusTarget {
  /** Crosshair y in candle-pane pixels; the candle pane's top is the chart's top. */
  y: number
  /** Crosshair x, kept so holding the label on hover keeps the vertical line in place. */
  x: number | null
  price: number
  /** `price` at the chart's precision, for "Add alert at $4.52". */
  text: string
}

/** An alert price: rounded to `precision`, positive and finite, else null. */
export function alertPrice(
  value: number | null | undefined,
  precision: number,
): { price: number; text: string } | null {
  if (value == null || !Number.isFinite(value)) return null
  const text = value.toFixed(precision)
  const price = Number(text)
  return price > 0 ? { price, text } : null
}

/** klinecharts cleared its crosshair: the pointer reached the price axis (KC:12743-12746)
 *  or a DOM element over the canvas, such as the "+" itself (KC:12938-12941). */
export function isClearedCrosshair(c: { y?: number; paneId?: string }): boolean {
  return c.y == null || !c.paneId
}

/** The "+" for a crosshair, or null: another pane, a drawing armed, no positive price. */
export function plusTarget(input: {
  crosshair: { x?: number; y?: number; paneId?: string }
  drawing: boolean
  priceAt: (y: number) => number | null | undefined
  precision: number
}): PlusTarget | null {
  const c = input.crosshair
  if (input.drawing || c.y == null || c.paneId !== CANDLE_PANE_ID) return null
  const p = alertPrice(input.priceAt(c.y), input.precision)
  return p ? { y: c.y, x: c.x ?? null, ...p } : null
}

/** A latched "+" stays while the pointer is within LATCH_BAND_PX of its row. */
export function keepsLatch(target: PlusTarget, pointerY: number): boolean {
  return Math.abs(pointerY - target.y) <= LATCH_BAND_PX
}

/** Where the "+" sits: against the axis's inner edge, centred on the crosshair's y.
 *  Null with no axis strip or off the pane. */
export function plusPlacement(input: {
  y: number
  axis: { left: number; top: number; width: number; height: number } | null
  axisOnLeft: boolean
}): { left: number; top: number } | null {
  const { axis, y } = input
  if (!axis || axis.width <= 0 || y < 0 || y > axis.height) return null
  return {
    left: input.axisOnLeft ? axis.left + axis.width : axis.left - PLUS_BUTTON.width,
    top: Math.round(axis.top + y - PLUS_BUTTON.height / 2),
  }
}
